import { useEffect, useState } from 'react';
import type { CrudFilter } from '@refinedev/core';
import { Input } from 'antd';
import { getFilterValue, type SetFilters } from '@/utils/filters';
import { FilterPanel } from './FilterPanel';

type TextFilterProps = {
  field: string;
  filters: CrudFilter[];
  confirm: () => void;
  clearFilters?: () => void;
  setFilters: SetFilters;
  placeholder?: string;
  width?: number;
};

/**
 * Free-text column filter, applied as `field[eq]`.
 *
 * Writes through `setFilters` rather than antd's own filter state — see
 * `useAdminTable` for why the two must not be mixed.
 */
export const TextFilter = ({
  field,
  filters,
  confirm,
  clearFilters,
  setFilters,
  placeholder,
  width = 200,
}: TextFilterProps) => {
  const current = getFilterValue<string>(filters, field, 'eq');
  const [pending, setPending] = useState<string | undefined>(current);

  useEffect(() => setPending(current), [current]);

  const handleFilter = () => {
    setFilters(
      [{ field, operator: 'eq', value: pending || undefined }],
      'merge'
    );
    confirm();
  };

  const handleReset = () => {
    setPending(undefined);
    setFilters([{ field, operator: 'eq', value: undefined }], 'merge');
    clearFilters?.();
  };

  return (
    <FilterPanel onFilter={handleFilter} onReset={handleReset}>
      <Input
        value={pending}
        onChange={(e) => setPending(e.target.value)}
        onPressEnter={handleFilter}
        placeholder={placeholder}
        style={{ width }}
        allowClear
      />
    </FilterPanel>
  );
};
